import { computed, inject } from '@angular/core';
import { AuthenticationSelectors } from '@next/core-lib/authentication';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { filter, map, pipe, switchMap, take, tap } from 'rxjs';

import { AssociatedChainPartnerService } from '../services/associated-chainpartner.service';
import { AssociatedChainPartner } from '../models/models';

interface AssociatedChainPartnerState {
  associatedChainPartner: AssociatedChainPartner | undefined;
  loadStatus: 'pending' | 'loading' | 'loaded' | 'error';
}

const initialState: AssociatedChainPartnerState = {
  associatedChainPartner: undefined,
  loadStatus: 'pending',
};

export const AssociatedChainPartnerStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, service = inject(AssociatedChainPartnerService)) => ({
    retrieveAssociatedChainPartner: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loadStatus: 'loading' })),
        switchMap(() =>
          service.retrieveAssociatedChainPartner().pipe(
            tapResponse({
              next: (associatedChainPartner) =>
                patchState(store, { associatedChainPartner, loadStatus: 'loaded' }),
              error: () => patchState(store, { loadStatus: 'error' }),
            }),
          ),
        ),
      ),
    ),
  })),
  withComputed((store) => ({
    associatedChainPartnerId: computed(() => store.associatedChainPartner()?.id),
    associatedChainPartnerName: computed(() => store.associatedChainPartner()?.name),
  })),
  withHooks({
    onInit(store, appStore = inject(Store)) {
      store.retrieveAssociatedChainPartner(
        appStore.select(AuthenticationSelectors.isLoggedIn).pipe(
          filter(Boolean),
          take(1),
          map(() => undefined),
        ),
      );
    },
  }),
);
